<script lang="ts" setup>
	import { useProductsBenefits } from '@/composables/usePlanBenefits'

	const {
		viewAll = true,
		defaultBtn = true,
		...props
	} = defineProps<{
		product: ProductsResponse | null
		loading?: boolean
		viewAll?: boolean
		defaultBtn?: boolean
	}>()
	const {
		productDetails,
		productCategory,
		groupId,
		productCode,
		productProvider: provider
	} = useProductDetails(computed(() => props.product || null))
	const { getBenefit } = useProductsBenefits()
	const isAllBenefit = shallowRef(false)
	const openHospitalList = shallowRef(false)
	const emits = defineEmits<{
		(arg: 'proceed'): void
		(event: 'view-all'): void
	}>()
	const benefits = ref(getBenefit(productCode.value)?.short)
	const currentOpen = shallowRef('Need To Know')

	const openBenefit = (arg: string) => {
		currentOpen.value = arg
	}
	const viewBenefits = () => {
		isAllBenefit.value = true
		emits('view-all')
	}
	watch(
		() => props.product,
		() => {
			benefits.value = getBenefit(productCode.value)?.short
		}
	)
</script>

<template>
	<div>
		<a-skeleton-button v-if="loading || !benefits" size="large" class="h-[200px] rounded-lg" block active loading />

		<template v-else>
			<div class="rounded-md border border-success-100 bg-primary-25 p-6 md:p-[30px_77px]">
				<ul>
					<li
						v-for="(item, k) in benefits"
						:key="k"
						class="list custom-transition mb-5 border-b border-gray-200"
					>
						<div class="flex justify-between">
							<div class="flex items-start gap-x-3">
								<div
									class="flex h-5 w-5 flex-none items-center justify-center rounded-full border border-success-500 bg-success-50"
								>
									<lazy-tools-icon
										:icon="item.icon.name"
										:dir="item.icon.dir"
										:height="12"
										:width="12"
									/>
								</div>

								<div>
									<div class="text-sm font-semibold text-slate-600">
										{{ k }}
									</div>

									<div
										class="custom-transition mb-4 overflow-y-auto"
										:class="{
											'max-h-[0]': k !== currentOpen,
											'max-h-[230px]': k === currentOpen
										}"
									>
										<div
											v-for="(detail, index) in item.value"
											:key="index"
											class="flex items-start gap-x-2 pb-3 text-sm first:mt-5"
										>
											<lazy-tools-icon icon="check" dir="fill" class="mt-1" />
											<template v-if="typeof detail === 'string'">
												<div
													class="text-sm font-normal leading-5 tracking-wide text-gray-600"
													v-html="detail"
												/>
											</template>
											<template v-else>
												<div>
													<div
														class="text-sm font-normal leading-4 text-gray-600"
														v-html="detail.text"
													/>

													<button
														v-if="item?.key === 'hospital'"
														type="button"
														aria-label="view hospital list"
														class="pt-3 font-medium text-primary-2"
														@click="openHospitalList = true"
													>
														View Hospital List
													</button>

													<nuxt-link
														v-if="detail.link"
														:to="detail.link.url"
														class="mt-2.5 inline-block border-b border-dashed border-primary-2 text-primary-2 hover:text-primary-500"
													>
														{{ detail.link.text }}
													</nuxt-link>
												</div>
											</template>
										</div>
									</div>
								</div>
							</div>

							<lazy-tools-icon
								dir="arrows"
								icon="caret-down"
								path-class="fill-gray-600"
								class="mt-1 cursor-pointer"
								:svg-class="`${k === currentOpen ? 'rotate-180' : 'rotate-0'} custom-transition`"
								:height="13"
								:width="13"
								@click="openBenefit(String(k))"
							/>
						</div>
					</li>
					<li v-if="viewAll" class="mt-4">
						<button
							type="button"
							aria-label="view benefits"
							class="font-medium tracking-wide text-primary-2 hover:text-primary-500"
							@click="viewBenefits"
						>
							View benefits
						</button>
					</li>
				</ul>
			</div>
		</template>
		<slot name="button">
			<button
				v-if="defaultBtn"
				type="button"
				data-testid="proceed"
				class="default-btn mt-12 w-full justify-center bg-primary-2 !outline-success-200"
				:disabled="loading"
				@click="$emit('proceed')"
			>
				Get Covered
			</button>
		</slot>
		<products-all-benefit-dialog v-model:show="isAllBenefit" :product-code :product-category :provider :group-id />
		<dialogs
			title="Hospital List"
			v-model:show="openHospitalList"
			modal-class="max-w-[700px]"
			:ui="{ modal: 'min-h-[300px]', title: 'py-5' }"
		>
			<products-hospital-list
				v-if="openHospitalList"
				:product-id="productDetails?.id ?? ''"
				class="min-h-[100px]"
			/>
		</dialogs>
	</div>
</template>
